import type { AgentCard } from "@opencloud/agent-protocol";
import type {
  ConversationQuestion,
  ConversationRun,
  ChatSnapshot as ConversationSnapshot,
} from "./types.js";

export function activeRunCard(
  snapshot: ConversationSnapshot,
  run: ConversationRun,
  personalAiAccounts = true,
): AgentCard | null {
  const question =
    snapshot.question?.runId === run.id ? snapshot.question : null;
  if (question) return questionCard(question);

  if (
    run.status === "paused" &&
    run.blocker?.requiredAction === "reconnect_provider"
  ) {
    return reconnectCard(run, personalAiAccounts);
  }
  return null;
}

export function questionCard(question: ConversationQuestion): AgentCard {
  if (question.reason === "material_decision") {
    return {
      id: `question:${question.questionId}`,
      type: "question_card",
      revision: 1,
      state: "live",
      icon: "question",
      title: question.question,
      subtitle: "Your answer",
      styleColor: "blue",
      sections: [
        {
          id: "context",
          type: "markdown",
          markdown: question.context ?? reasonDetail(question.reason),
          display: "plain",
        },
        ...(question.options.length > 0
          ? [
              {
                id: "answers",
                type: "choices" as const,
                options: question.options.map((option) => ({
                  id: option.id,
                  label: option.label,
                  detail: option.detail,
                  ...(option.tone === "primary" ? { recommended: true } : {}),
                })),
                customAnswer: {
                  label: "Answer with your own words",
                  placeholder: "Write your answer…",
                  actionLabel: "Answer",
                },
              },
            ]
          : [
              {
                id: "reply",
                type: "choices" as const,
                options: [
                  {
                    id: "reply-in-composer",
                    label: "Answer in the message box",
                    detail: "Write what you want in your own words below.",
                  },
                  {
                    id: "ask-for-help",
                    label: "Ask for help deciding",
                    detail:
                      "The agent can explain the tradeoffs before you choose.",
                  },
                ],
                customAnswer: {
                  label: "Answer with your own words",
                  placeholder: "Write your answer…",
                  actionLabel: "Answer",
                },
              },
            ]),
      ],
    };
  }

  return {
    id: `question:${question.questionId}`,
    type: "permission_card",
    revision: 1,
    state: "live",
    icon: "permission",
    title: question.question,
    subtitle:
      question.reason === "new_authority"
        ? "Permission needed"
        : "Your permission",
    styleColor: "orange",
    sections: [
      {
        id: "context",
        type: "markdown",
        markdown: question.context ?? reasonDetail(question.reason),
        display: "box",
        styleColor: "orange",
      },
      ...(question.options.length > 0
        ? [
            {
              id: "actions",
              type: "actions" as const,
              actions: question.options.map((option) => ({
                id: option.id,
                label: option.label,
                action: "answer_question",
                style:
                  option.tone === "destructive"
                    ? ("destructive" as const)
                    : option.tone === "primary"
                      ? ("primary" as const)
                      : ("secondary" as const),
              })),
            },
          ]
        : []),
    ],
  };
}

function reconnectCard(run: ConversationRun, personalAiAccounts: boolean): AgentCard {
  const provider = readable(run.blocker?.provider ?? "model provider");
  return {
    id: `action:${run.id}`,
    type: "action_required_card",
    revision: 1,
    state: "live",
    icon: "connection",
    title: `Reconnect ${provider}`,
    subtitle: "Action required",
    styleColor: "orange",
    sections: [
      {
        id: "details",
        type: "markdown",
        // Organisation installations have no personal accounts; an admin reconnects.
        markdown: personalAiAccounts
          ? "Your work is **saved**. Reconnect this account in Agent settings and the run continues from the same workspace."
          : "Your work is **saved**. Ask an admin to reconnect this account in the dashboard and the run continues from the same workspace.",
        display: "box",
        styleColor: "orange",
      },
    ],
  };
}

function reasonDetail(reason: ConversationQuestion["reason"]): string {
  if (reason === "new_authority") {
    return "This goes beyond the authority already granted, so the agent will not continue without your choice.";
  }
  if (reason === "policy") {
    return "The work is saved. The agent needs permission for this specific action before it can continue.";
  }
  return "Your answer changes what people will experience, so the agent is asking instead of choosing for you.";
}

function readable(value: string): string {
  const normalized = value.replace(/[._-]+/g, " ");
  if (normalized.toLowerCase() === "openai") return "OpenAI";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}
